import {
  normalizePayoutPricingSide,
  PAYOUT_PRICING_SIDE_DEFAULT,
  PAYOUT_PRICING_SIDES,
} from "constants/payout";
import {
  buildPayoutAnalysisQueryParams,
  fetchPayoutAnalysisResolved,
  getPayoutAnalysisData,
} from "helpers/payoutAnalysisApi";
import { copyToClipboard, toast } from "helpers/generalHelper";

/**
 * Clipboard URL for a payout share — always `share.url` from the API.
 * Never rebuild from token + origin / REACT_APP_FRONT_END_URL.
 */
export function resolvePayoutShareUrl(share) {
  const rawUrl = typeof share?.url === "string" ? share.url.trim() : "";
  if (!rawUrl) return null;

  return rawUrl.replace(/\\\//g, "/").replace(/\/$/, "");
}

export function formatDisplayText(text) {
  if (text == null) return "";
  return String(text).replace(/<[^>]*>/g, "").replace(/\\\//g, "/").trim();
}

export function stripHtml(text) {
  return formatDisplayText(text);
}

export function getShareExpiryDate(share) {
  const raw = share?.expires_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isShareExpired(share, now = Date.now()) {
  const expiry = getShareExpiryDate(share);
  if (!expiry) return false;
  return expiry.getTime() <= now;
}

export function formatShareExpiryLabel(expiresAt) {
  const date = expiresAt instanceof Date ? expiresAt : getShareExpiryDate({ expires_at: expiresAt });
  if (!date) return null;

  return `Link expires at ${date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  })} UTC`;
}

export function formatShareTimeRemaining(expiresAt, now = Date.now()) {
  const date = expiresAt instanceof Date ? expiresAt : getShareExpiryDate({ expires_at: expiresAt });
  if (!date) return null;

  const ms = date.getTime() - now;
  if (ms <= 0) return "Expired";

  const totalMinutes = Math.ceil(ms / 60000);
  if (totalMinutes < 60) {
    return `Expires in ${totalMinutes} minute${totalMinutes === 1 ? "" : "s"}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) {
    return `Expires in ${hours} hour${hours === 1 ? "" : "s"}`;
  }

  return `Expires in ${hours}h ${minutes}m`;
}

export function formatShareTtlLabel(ttlSeconds) {
  const seconds = Number(ttlSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  if (seconds < 3600) {
    const minutes = Math.max(1, Math.round(seconds / 60));
    return `Valid for ${minutes} minute${minutes === 1 ? "" : "s"} after sharing`;
  }

  const hours = Math.round(seconds / 3600);
  return `Valid for ${hours} hour${hours === 1 ? "" : "s"} after sharing`;
}

export function getShareExpiryDelayMs(share, now = Date.now()) {
  const expiry = getShareExpiryDate(share);
  if (!expiry) return null;
  return expiry.getTime() - now;
}

function normalizeAvailableSides(sides) {
  if (!Array.isArray(sides) || sides.length === 0) return null;
  const normalized = [
    ...new Set(
      sides
        .map((side) => normalizePayoutPricingSide(side, null))
        .filter(Boolean)
    ),
  ];
  return normalized.length > 0 ? normalized : null;
}

/**
 * Parse API `available_side`: `bid` | `ask` | `both`.
 */
export function parseAvailablePayoutSide(apiValue) {
  const normalized = String(apiValue ?? "")
    .trim()
    .toLowerCase();

  if (normalized === PAYOUT_PRICING_SIDES.BID) {
    return [PAYOUT_PRICING_SIDES.BID];
  }
  if (normalized === PAYOUT_PRICING_SIDES.ASK) {
    return [PAYOUT_PRICING_SIDES.ASK];
  }
  if (normalized === "both") {
    return [PAYOUT_PRICING_SIDES.BID, PAYOUT_PRICING_SIDES.ASK];
  }

  return null;
}

/**
 * Infer bid/ask availability from shorthand when API omits `available_side`.
 */
export function inferAvailablePayoutSidesFromShorthand(shorthand) {
  if (!shorthand) {
    return [PAYOUT_PRICING_SIDES.BID, PAYOUT_PRICING_SIDES.ASK];
  }

  const raw = String(shorthand);
  const text = stripHtml(raw).toLowerCase();
  const hasAsk = /\bask\b/.test(text) || /\bask\b/i.test(raw);
  const hasBid = /\bbid\b/.test(text) || /\bbid\b/i.test(raw);

  if (hasBid && !hasAsk) {
    return [PAYOUT_PRICING_SIDES.BID];
  }
  if (hasAsk && !hasBid) {
    return [PAYOUT_PRICING_SIDES.ASK];
  }

  return [PAYOUT_PRICING_SIDES.BID, PAYOUT_PRICING_SIDES.ASK];
}

export function getAvailablePayoutPricingSides(bootstrap) {
  const fromApiSide = parseAvailablePayoutSide(
    bootstrap?.available_side ?? bootstrap?.share?.available_side
  );
  if (fromApiSide) {
    return fromApiSide;
  }

  const apiSides = normalizeAvailableSides(
    bootstrap?.available_sides ?? bootstrap?.share?.available_sides
  );
  if (apiSides) {
    return apiSides;
  }

  return inferAvailablePayoutSidesFromShorthand(bootstrap?.shorthand);
}

export function getDefaultPayoutPricingSideFromBootstrap(
  bootstrap,
  fallback = PAYOUT_PRICING_SIDE_DEFAULT
) {
  const availableSides = getAvailablePayoutPricingSides(bootstrap);
  if (availableSides.length === 1) {
    return availableSides[0];
  }

  const current = normalizePayoutPricingSide(fallback, null);
  if (current && availableSides.includes(current)) {
    return current;
  }

  return PAYOUT_PRICING_SIDE_DEFAULT;
}

export function getShareInitialDaysElapsed(bootstrap) {
  const elapsed =
    bootstrap?.left_side?.days_elapsed ?? bootstrap?.grid?.days_elapsed;
  return typeof elapsed === "number" ? elapsed : 0;
}

export function buildShareFetchSnapshot({
  pricingSide,
  daysElapsed,
  apiMetric,
  gridParams,
}) {
  return {
    pricingSide: normalizePayoutPricingSide(pricingSide),
    daysElapsed,
    apiMetric,
    gridParams: {
      chart_increment: gridParams?.chart_increment ?? null,
      starting_ul_settlement_price:
        gridParams?.starting_ul_settlement_price ?? null,
    },
  };
}

export function matchesShareFetchSnapshot(current, snapshot) {
  if (!snapshot) return false;

  return (
    normalizePayoutPricingSide(current.pricingSide) === snapshot.pricingSide &&
    current.daysElapsed === snapshot.daysElapsed &&
    current.apiMetric === snapshot.apiMetric &&
    Number(current.gridParams?.chart_increment ?? 0) ===
      Number(snapshot.gridParams?.chart_increment ?? 0) &&
    Number(current.gridParams?.starting_ul_settlement_price ?? NaN) ===
      Number(snapshot.gridParams?.starting_ul_settlement_price ?? NaN)
  );
}

/**
 * Clipboard format: backend `shorthand` as-is + `share.url`.
 * Do not append bid/ask — the API shorthand already includes the side.
 */
export function formatShorthandWithAnalysisLink(shorthand, shareUrl) {
  const cleanShorthand = stripHtml(shorthand);

  if (!cleanShorthand) {
    return shareUrl;
  }

  return `${cleanShorthand}  ${shareUrl}`;
}

export function copyShorthandWithAnalysisFromShare(shorthand, share) {
  if (isShareExpired(share)) {
    throw new Error("This payout analysis link has expired. Open Payout Analysis again to get a new link.");
  }

  const url = resolvePayoutShareUrl(share);
  if (!url) {
    throw new Error("Share link unavailable. Open Payout Analysis first.");
  }

  const text = formatShorthandWithAnalysisLink(shorthand, url);
  copyToClipboard(text);
  toast("Copied with payout analysis link", "success");
  return url;
}

/**
 * Loads payout analysis then copies backend `data.shorthand` + `share.url`.
 */
export async function fetchAndCopyShorthandWithAnalysis(
  summaryId,
  shorthand,
  side = PAYOUT_PRICING_SIDE_DEFAULT
) {
  const params = buildPayoutAnalysisQueryParams({
    side,
    metric: "P&L",
  });
  const response = await fetchPayoutAnalysisResolved(summaryId, params);
  const data = getPayoutAnalysisData(response);

  return copyShorthandWithAnalysisFromShare(
    data?.shorthand || shorthand,
    data?.share
  );
}
